import { config } from '@/lib/config';
import { buildAircraftMarkerEl } from '@/lib/map/markers/aircraftIcon';
import { buildDotMarkerEl } from '@/lib/map/markers/dotMarker';
import { buildDroneMarkerHtml } from '@/lib/map/markers/droneIcon';
import {
  buildDarkCardHtml,
  buildLightCardHtml,
  categoryTypeLabel,
} from '@/lib/map/markers/popupCards';
import { getStartCenter } from '@/lib/map/userLocation';
import { useUiStore } from '@/lib/store/ui';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef } from 'react';
import type { LiveObject } from '../utils/objectAccumulator';

export interface ZoomTarget {
  bounds: [number, number][];
  key: number;
  /** 'fit' = show-all (topic select): Surveillance-parity wide fit, capped so
   *  clustered objects don't over-zoom. 'focus' = message click: zoom in close. */
  mode: 'fit' | 'focus';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** Surveillance-parity hover card for a live map object. */
function cardFor(o: LiveObject): string {
  if (o.kind === 'aircraft') {
    const emitter = escapeHtml(String(o.fields.emitter ?? '—'));
    const rows: Array<[string, string]> = [
      ['Type', categoryTypeLabel(o.fields.category as string | undefined)],
      ['ASL', o.altitudeFt != null ? `${Math.round(o.altitudeFt).toLocaleString()} ft` : '—'],
      ['Speed', o.fields.gs != null ? `${Math.round(Number(o.fields.gs))} kts` : '—'],
      ['Hdg', o.heading != null ? `${Math.round(o.heading)}°` : '—'],
      ['Src', `${emitter} · 1 src`],
      ['Trust', '—'],
    ];
    return buildDarkCardHtml(o.label, rows);
  }
  if (o.kind === 'drone') {
    const rows: Array<[string, string]> = [
      ['Type', 'Drone'],
      ['ASL', o.altitudeFt != null ? `${Math.round(o.altitudeFt)} m` : '—'],
      [
        'Speed',
        o.fields.ground_speed != null ? `${Math.round(Number(o.fields.ground_speed))} m/s` : '—',
      ],
      ['Hdg', o.heading != null ? `${Math.round(o.heading)}°` : '—'],
      [
        'Status',
        o.staleState === 'lost'
          ? '<span style="color:#f87171">LOST</span>'
          : '<span style="color:#4ade80">Live</span>',
      ],
    ];
    return buildDarkCardHtml(o.label, rows);
  }
  return buildLightCardHtml(
    o.label,
    String(o.fields.service ?? ''),
    // Match the uniform green dot markers — surveillance "ADS-B green".
    '#16a34a',
    o.fields.model ? String(o.fields.model) : o.label,
  );
}

interface ActiveMarker {
  marker: mapboxgl.Marker;
  el: HTMLElement;
  key: string;
  obj: LiveObject;
  noRotate: boolean;
}

export function useDsMap(objects: LiveObject[], zoomTarget: ZoomTarget | null) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const activeRef = useRef<Map<string, ActiveMarker>>(new Map());
  const basemap = useUiStore((s) => s.basemap);
  // Starts at the mount-time basemap (the map was created with it), so the
  // sync effect only setStyle()s on an actual change, not redundantly on mount.
  const lastBasemapRef = useRef<string>(useUiStore.getState().basemap);

  // init / teardown (unchanged from the current useDsMap pattern)
  useEffect(() => {
    if (!containerRef.current) return;
    mapboxgl.accessToken = config.map.mapboxToken;
    // Same start as the main map: last known user location — ENG-277.
    const [lat, lng] = getStartCenter();
    const map = new mapboxgl.Map({
      container: containerRef.current,
      // Share the user's persisted basemap (Satellite, Dark, …) with the main
      // map instead of forcing the default. Read imperatively so changing the
      // basemap doesn't re-init the whole map (see the setStyle effect below).
      style: useUiStore.getState().basemap,
      center: [lng, lat],
      zoom: config.map.defaultZoom,
      attributionControl: false,
    });
    mapRef.current = map;
    const container = containerRef.current;
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(container);
    const raf = requestAnimationFrame(() => map.resize());
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      for (const a of activeRef.current.values()) a.marker.remove();
      activeRef.current.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Follow basemap changes (from the shared LayerPanel). setStyle swaps only
  // GL sources/layers — the DS markers are DOM mapboxgl.Markers (overlays), so
  // they survive the swap and need no re-attach.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (lastBasemapRef.current === basemap) return;
    lastBasemapRef.current = basemap;
    map.setStyle(basemap);
  }, [basemap]);

  // diff markers from the live object set; create/update/remove by id+kind.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set<string>();

    for (const o of objects) {
      seen.add(o.id);
      const dim = o.staleState === 'dim';
      const key = `${o.kind}|${o.staleState}`;
      let entry = activeRef.current.get(o.id);

      if (!entry || entry.key !== key) {
        // (re)build the element for this kind/state
        entry?.marker.remove();
        let el: HTMLElement;
        let anchor: mapboxgl.Anchor = 'center';
        let noRotate = false;
        if (o.kind === 'aircraft') {
          const built = buildAircraftMarkerEl(o, dim);
          el = built.el;
          noRotate = built.noRotate;
        } else if (o.kind === 'drone') {
          const status = o.staleState === 'lost' ? 'LOST' : 'Active';
          const built = buildDroneMarkerHtml(
            { status, callsign: o.label, heading: o.heading ?? 0 },
            false,
            false,
            0,
          );
          el = document.createElement('div');
          el.style.cssText = `width:${built.boxSize}px;height:${built.boxSize}px;cursor:pointer`;
          el.innerHTML = built.html;
        } else {
          // Dots are rendered as uniform green markers regardless of the
          // object's per-service colour (no outer rings — see buildDotMarkerEl).
          // #16a34a is the surveillance "ADS-B green" (SERVICE_COLOR.live_ads).
          el = buildDotMarkerEl({ colour: '#16a34a', radius: 7 });
          anchor = 'center';
        }
        const marker = new mapboxgl.Marker({ element: el, anchor })
          .setLngLat([o.lng, o.lat])
          .addTo(map);
        entry = { marker, el, key, obj: o, noRotate };
        const activeEntry = entry;
        const tip = document.createElement('div');
        tip.style.cssText =
          'position:absolute;bottom:calc(100% + 8px);left:50%;transform:translateX(-50%);z-index:600;display:none';
        el.appendChild(tip);
        // Mapbox gives each marker a screen-Y-based inline z-index (southern
        // markers paint in front), so neighbouring markers can render over the
        // tooltip. Lift the hovered marker — and its tooltip child — above all
        // siblings on hover, then restore Mapbox's value on leave.
        let prevZ = '';
        el.addEventListener('mouseenter', () => {
          tip.innerHTML = cardFor(activeEntry.obj);
          tip.style.display = 'block';
          prevZ = el.style.zIndex;
          el.style.zIndex = '10000';
        });
        el.addEventListener('mouseleave', () => {
          tip.style.display = 'none';
          el.style.zIndex = prevZ;
        });
        activeRef.current.set(o.id, entry);
      } else {
        entry.marker.setLngLat([o.lng, o.lat]); // move in place
        entry.obj = o; // keep obj current so popup reflects live data
      }
      // rotate aircraft/drone by heading
      if (o.heading != null && (o.kind === 'aircraft' || o.kind === 'drone')) {
        if (o.kind === 'drone' || !entry.noRotate) {
          const svg = entry.el.querySelector('svg');
          if (svg)
            (svg as SVGElement).style.transform =
              `translate(-50%,-50%) rotate(${Math.round(o.heading)}deg)`;
        }
      }
    }

    for (const [id, a] of activeRef.current) {
      if (!seen.has(id)) {
        a.marker.remove();
        activeRef.current.delete(id);
      }
    }
  }, [objects]);

  // Apply a zoom target. 'fit' (topic show-all) mirrors the Surveillance tab —
  // fitBounds with generous padding and a maxZoom cap so a tight cluster of
  // objects doesn't over-zoom. 'focus' (message click) zooms in close.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !zoomTarget || zoomTarget.bounds.length === 0) return;
    const fit = zoomTarget.mode === 'fit';
    if (zoomTarget.bounds.length === 1) {
      const center = zoomTarget.bounds[0] as [number, number];
      if (fit) {
        // Lone object: settle at the show-all cap so it's consistent across
        // topics regardless of the map's prior zoom.
        map.easeTo({ center, zoom: 11, duration: 600 });
      } else {
        map.flyTo({ center, zoom: 14, duration: 600 });
      }
    } else {
      const b = new mapboxgl.LngLatBounds();
      for (const c of zoomTarget.bounds) b.extend(c as [number, number]);
      map.fitBounds(b, {
        padding: fit ? 60 : 40,
        maxZoom: fit ? 11 : 14,
        animate: true,
        duration: 600,
      });
    }
  }, [zoomTarget]);

  return containerRef;
}
